import React, { useState, useRef, useEffect } from "react";
import { Modal } from "react-bootstrap";
import style from "./MyApp.module.scss";
import { YoomDateTimePicker } from "../../templates/YoomDateTimePicker/YoomDateTimePicker";
import { DirectUpload } from "@rails/activestorage";
import { getImageUrl } from "../../../commons/assets_path";
import { Loader } from "../../common/Loaders/Loader";
import axios from "axios";
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
import { useTranslation } from "react-i18next";

type Props = {
  clientCode: string;
  serviceCode: string;
  serviceName: string;
  modalRef: { show: boolean };
  closeModal: () => void;
  redirectUrl?: string;
};

type ConfigVariable = {
  id: number;
  display_name: string;
  translate_display_name: string;
  data_type: string;
  description: string;
  translate_description: string;
  default_value: string;
  required: boolean;
};

type MyApp = {
  client_id: number;
  owner_id: number;
  service_name: string;
  service_code: string;
  connect_type: string;
  account_id: string;
  access_token: string;
  expired_at: string;
  client_certificate_file?: string;
  client_certificate_file_password?: string;
  options: { [key: string]: string };
};

type AccessTokenOptions = {
  display: boolean;
  display_name: string;
  translate_display_name: string;
};

type Error = {
  [key: string]: string[];
};

interface Blob {
  signed_id: string;
}

interface Upload {
  create: (callback: (error: Error | null, blob: Blob) => void) => void;
}

const createUploadPromise = (upload: Upload): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    upload.create((error, blob) => {
      if (error) {
        reject(error);
      } else {
        resolve(blob);
      }
    });
  });
};

const MyAppCreateForm = ({ clientCode, serviceCode, serviceName, modalRef, closeModal, redirectUrl }: Props) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<Error>({});
  const [formData, setFormData] = useState<MyApp | undefined>(undefined);
  const [connectingServiceNote, setConnectingServiceNote] = useState<string>("");
  const [configVariables, setConfigVariables] = useState<ConfigVariable[]>([]);
  const [directUploadsUrl, setDirectUploadsUrl] = useState<string>("");
  const [isClientCertificateAuthConfigEnabled, setIsClientCertificateAuthConfigEnabled] = useState<boolean>(false);
  const [accessTokenOptions, setAccessTokenOptions] = useState<AccessTokenOptions>({
    display: false,
    display_name: null,
    translate_display_name: null,
  });
  const [loading, setLoading] = useState<boolean>(true);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (!formData) return;
    const file = e.target.files?.[0];
    if (file) {
      const upload = new DirectUpload(file, directUploadsUrl);
      try {
        const blob = await createUploadPromise(upload);
        setFormData({ ...formData, client_certificate_file: blob.signed_id });
      } catch (error) {
        // 何もしない
      }
    }
  };

  const createMyApp = async () => {
    if (!formData) return;
    const path =
      formData.connect_type === "oauth" ? `/${clientCode}/my_apps/oauth2_authorize` : `/${clientCode}/my_apps`;
    await axios
      .post(path, { my_app: formData })
      .then((response) => {
        if (response.data.redirect_url) {
          window.location.href = response.data.redirect_url;
        }
      })
      .catch((error) => {
        if (error.response?.status === 422) {
          setError(error.response.data.errors);
        }
      });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const path = `/${clientCode}/my_apps/new`;
        const params = redirectUrl
          ? { service_code: serviceCode, previous_url: redirectUrl }
          : { service_code: serviceCode };
        const response = await axios.get(path, { params: params });
        setConnectingServiceNote(response.data.connecting_service_note);
        setConfigVariables(response.data.connect_config_variables);
        setDirectUploadsUrl(response.data.direct_uploads_url);
        const myApp = response.data.my_app;
        setIsClientCertificateAuthConfigEnabled(response.data.is_client_certificate_auth_config_enabled);
        setAccessTokenOptions(response.data.access_token_options);
        if (myApp) {
          setFormData({
            client_id: myApp.client_id,
            owner_id: myApp.owner_id,
            service_name: myApp.service_name,
            service_code: myApp.service_code,
            connect_type: myApp.connect_type,
            account_id: myApp.connect_type === "oauth" ? myApp.account_id : "",
            access_token: myApp.access_token || "",
            expired_at: myApp.expired_at,
            options: {},
            ...(isClientCertificateAuthConfigEnabled && {
              client_certificate_file: myApp.client_certificate_file || "",
              client_certificate_file_password: myApp.client_certificate_file_password || "",
            }),
          });
        }
      } catch (error) {
        // 何もしない
      }
    };

    const handleTrixContent = () => {
      const trix = document.querySelector(".js-trix-content");
      if (trix) {
        const links = trix.querySelectorAll("a");
        links.forEach((link) => {
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noopener noreferrer");
        });
        const files = trix.querySelectorAll("ul, ol, dl, .attachment, .attachment__caption");
        files.forEach((file) => {
          const brTags = file.querySelectorAll("br");
          brTags.forEach((br) => br.remove());
        });
      }
    };
    if (modalRef.show) {
      fetchData();
      handleTrixContent();
      setLoading(false);
    }
  }, [modalRef.show]);

  return (
    <>
      {/* @ts-ignore */}
      <Modal
        show={modalRef.show}
        onHide={closeModal}
        scrollable
        size="lg"
        centered
        animation={false}
        contentClassName={style.modalContent}
      >
        {/* @ts-ignore */}
        <Modal.Header>
          {/* @ts-ignore */}
          <Modal.Title style={{ fontSize: "20px", marginBottom: "16px" }}>
            {serviceName}
            {t("MyAppCreateForm.の新規登録")}
          </Modal.Title>
        </Modal.Header>
        {/* @ts-ignore */}
        <Modal.Body>
          {loading || !formData ? (
            <>
              {/* @ts-ignore */}
              <Loader loaderColor="yoom-green" />
            </>
          ) : (
            <>
              <div className="form-group">
                <div className="js-trix-content" dangerouslySetInnerHTML={{ __html: connectingServiceNote }}></div>
              </div>
              {error?.base && (
                <ul className="border yoom-danger-border">
                  <li className="yoom-text-error list-unstyled">{error.base}</li>
                </ul>
              )}
              {formData.connect_type !== "oauth" && (
                <div className="form-group">
                  <div className={style.inputLabel}>
                    {t("MyAppCreateForm.アカウント名")}
                    <img src={getImageUrl("images/icons/asterisk.svg")} />
                    <span className={style.requiredText}>{t("MyAppCreateForm.必須")}</span>
                  </div>
                  <input
                    type="text"
                    id="my_app_account_id"
                    className={`form-control${error?.account_id ? " is-invalid" : ""}`}
                    name="my_app[account_id]"
                    onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                  />
                  {error?.account_id && <div className="invalid-feedback">{error.account_id.join(",")}</div>}
                </div>
              )}
              {accessTokenOptions.display && (
                <div className="form-group">
                  <div className={style.inputLabel}>
                    {accessTokenOptions.translate_display_name
                      ? accessTokenOptions.translate_display_name
                      : t("MyAppCreateForm.アクセストークン")}
                    <img src={getImageUrl("images/icons/asterisk.svg")} />
                    <span className={style.requiredText}>{t("MyAppCreateForm.必須")}</span>
                  </div>
                  <input
                    type="text"
                    id="my_app_access_token"
                    className={`form-control${error?.access_token ? " is-invalid" : ""}`}
                    name="my_app[access_token]"
                    onChange={(e) => setFormData({ ...formData, access_token: e.target.value })}
                  />
                  {error?.access_token && <div className="invalid-feedback">{error.access_token.join(",")}</div>}
                </div>
              )}
              {configVariables.map((configVariable) => (
                <div key={configVariable.id}>
                  {configVariable.data_type === "hidden" ? (
                    <div className="form-group">
                      <input
                        type="hidden"
                        name={`my_app[options][${configVariable.display_name}]`}
                        value={configVariable.default_value}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="form-group">
                        <div className={style.inputLabel}>
                          {configVariable.translate_display_name}
                          {configVariable.required && (
                            <>
                              <img src={getImageUrl("images/icons/asterisk.svg")} />
                              <span className={style.requiredText}>{t("MyAppCreateForm.必須")}</span>
                            </>
                          )}
                        </div>
                        {configVariable.data_type === "text" && (
                          <input
                            type="text"
                            className={`test-my_app_option_value form-control ${
                              error?.[`options[${configVariable.display_name}]`] ? "is-invalid" : ""
                            }`}
                            name={`my_app[options][${configVariable.display_name}]`}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                options: { ...formData.options, [configVariable.display_name]: e.target.value },
                              })
                            }
                          />
                        )}
                        {configVariable.data_type === "long_text" && (
                          <textarea
                            className={`test-my_app_option_value form-control ${
                              error?.[`options[${configVariable.display_name}]`] ? "is-invalid" : ""
                            }`}
                            name={`my_app[options][${configVariable.display_name}]`}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                options: { ...formData.options, [configVariable.display_name]: e.target.value },
                              })
                            }
                          />
                        )}
                        {configVariable.data_type === "number" && (
                          <input
                            type="number"
                            className={`test-my_app_option_value form-control ${
                              error?.[`options[${configVariable.display_name}]`] ? "is-invalid" : ""
                            }`}
                            name={`my_app[options][${configVariable.display_name}]`}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                options: { ...formData.options, [configVariable.display_name]: e.target.value },
                              })
                            }
                          />
                        )}
                        {configVariable.data_type === "date" && (
                          <>
                            {/* @ts-ignore */}
                            <YoomDateTimePicker datetime={false} target={ref} setDateFormat={() => {}} />
                            <input
                              ref={ref}
                              type="text"
                              className={`test-my_app_option_value form-control ${
                                error?.[`options[${configVariable.display_name}]`] ? "is-invalid" : ""
                              }`}
                              name={`my_app[options][${configVariable.display_name}]`}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  options: { ...formData.options, [configVariable.display_name]: e.target.value },
                                })
                              }
                              onFocus={() => {
                                setFormData({
                                  ...formData,
                                  options: {
                                    ...formData.options,
                                    [configVariable.display_name]: ref.current?.value || "",
                                  },
                                });
                              }}
                            />
                          </>
                        )}
                        {error?.[`options[${configVariable.display_name}]`] ? (
                          <div className="invalid-feedback">{error[`options[${configVariable.display_name}]`]}</div>
                        ) : (
                          <small className="form-text text-muted">{configVariable.translate_description}</small>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
              {isClientCertificateAuthConfigEnabled && (
                <>
                  <div className="form-group">
                    <div className={style.inputLabel}>{t("MyAppCreateForm.クライアント証明書ファイル")}</div>
                    <div className="custom-file">
                      <input
                        className={`custom-file-input js-file_test_value ${
                          error?.client_certificate_file ? "is-invalid" : ""
                        }`}
                        accept=".pfx,.p12"
                        type="file"
                        name="my_app[client_certificate_file]"
                        id="my_app_client_certificate_file"
                        onChange={handleFileUpload}
                      />
                      <label className="custom-file-label" htmlFor="my_app_client_certificate_file">
                        {t("MyAppCreateForm.ファイルを選択してください")}
                      </label>
                      {error?.client_certificate_file ? (
                        <div className="invalid-feedback">{error.client_certificate_file.join(",")}</div>
                      ) : (
                        <small className="form-text text-muted">
                          {t("MyAppCreateForm.client_certificate_requirements_message")}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <div className={style.inputLabel}>{t("MyAppCreateForm.クライアント証明書ファイルパスワード")}</div>
                    <input
                      type="text"
                      className={`form-control ${error?.client_certificate_file_password ? " is-invalid" : ""}`}
                      name="my_app[client_certificate_file_password]"
                      id="my_app_client_certificate_file_password"
                      onChange={(e) => setFormData({ ...formData, client_certificate_file_password: e.target.value })}
                    />
                    {error?.client_certificate_file_password ? (
                      <div className="invalid-feedback">{error.client_certificate_file_password.join(",")}</div>
                    ) : (
                      <small className="form-text text-muted">
                        {t("MyAppCreateForm.クライアント証明書認証を設定されている場合のみ必須です。")}
                      </small>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
        {/* @ts-ignore */}
        <Modal.Footer>
          <button type="button" onClick={closeModal} className="btn btn-secondary">
            {t("MyAppCreateForm.キャンセル")}
          </button>
          <button type="button" onClick={createMyApp} className="btn btn-info">
            {t("MyAppCreateForm.追加")}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MyAppCreateForm;
